import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Markdown } from "@/components/Markdown";
import { useSeo } from "@/hooks/useSeo";
import { ARTICLES, getArticle } from "@/data/blogArticles";

function BlogHeader() {
  return (
    <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-fib-4 sm:px-12">
      <Link to="/" data-testid="article-home-link">
        <Logo className="h-10" />
      </Link>
      <div className="flex items-center gap-fib-4 text-gr-sm font-semibold">
        <Link to="/blog" data-testid="article-blog-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Blog</Link>
        <Link to="/ambassadeur" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir ambassadeur</Link>
        <Link to="/" className="rounded-full bg-brand px-fib-4 py-fib-2 text-white hover:opacity-90">Faire ma demande</Link>
      </div>
    </header>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useSeo({
    title: article ? article.metaTitle : "Article — Blog PlanPermis",
    description: article ? article.description : undefined,
    image: article && article.image ? article.image : undefined,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          datePublished: article.datePublished,
          author: { "@type": "Organization", name: "PlanPermis" },
          publisher: { "@type": "Organization", name: "PlanPermis" },
          ...(article.image ? { image: article.image } : {}),
        }
      : undefined,
  });

  if (!article) return <Navigate to="/blog" replace />;

  const related = (article.related || []).map((s) => getArticle(s)).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <BlogHeader />

      <article className="px-6 py-fib-6 sm:px-12">
        <div className="mx-auto max-w-3xl">
          {/* Fil d'ariane */}
          <nav aria-label="Fil d'ariane" className="flex flex-wrap items-center gap-fib-2 text-gr-xs text-neutral-500" data-testid="breadcrumb">
            <Link to="/" className="hover:text-brand">Accueil</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-brand">Blog</Link>
            <span>/</span>
            <span className="text-neutral-700">{article.category}</span>
          </nav>

          <div className="mt-fib-4 flex items-center gap-fib-3 text-gr-xs font-semibold text-neutral-400">
            <span className="rounded-full bg-brand/10 px-fib-3 py-fib-1 text-brand">{article.category}</span>
            <span>{article.readingTime} min de lecture</span>
          </div>
          <h1 className="mt-fib-3 text-gr-2xl font-black tracking-tight text-neutral-900 sm:text-gr-3xl text-balance" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-fib-4 text-gr-lg text-neutral-600">{article.excerpt}</p>

          {article.image && (
            <img
              src={article.image}
              alt={article.title}
              className="mt-fib-5 aspect-[16/9] w-full rounded-2xl object-cover"
            />
          )}

          <div className="mt-fib-6">
            <Markdown content={article.body} />
          </div>

          {/* CTA */}
          <div className="mt-fib-7 rounded-2xl border border-brand/30 bg-brand/5 p-fib-6 text-center">
            <p className="text-gr-lg font-bold text-neutral-900">Trouvez une auto-école disponible rapidement</p>
            <p className="mt-fib-2 text-gr-sm text-neutral-600">Réserve ta date d'examen en ligne et dépose ton dossier.</p>
            <div className="mt-fib-4 flex flex-wrap justify-center gap-fib-3">
              <Link to="/" data-testid="article-cta" className="inline-flex items-center gap-fib-2 rounded-full bg-brand px-fib-6 py-fib-3 text-gr-base font-bold text-white hover:opacity-90">
                Faire ma demande <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/permis-accelere" className="inline-flex items-center rounded-full border border-neutral-300 bg-white px-fib-5 py-fib-3 text-gr-sm font-semibold text-neutral-700 hover:border-brand hover:text-brand">
                Voir le permis accéléré
              </Link>
            </div>
          </div>

          {/* Articles liés (maillage interne) */}
          {related.length > 0 && (
            <section className="mt-fib-7 border-t border-neutral-200 pt-fib-5" data-testid="related-articles">
              <p className="text-gr-xs font-bold uppercase tracking-[0.2em] text-neutral-400">À lire aussi</p>
              <div className="mt-fib-4 grid gap-fib-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group rounded-xl border border-neutral-200 bg-white p-fib-5 transition-colors hover:border-brand"
                  >
                    <span className="text-gr-xs font-semibold text-brand">{r.category}</span>
                    <p className="mt-fib-2 font-bold text-neutral-900 group-hover:text-brand">{r.title}</p>
                    <p className="mt-fib-2 text-gr-sm text-neutral-500">{r.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}
